import type { QueryClient } from "@tanstack/react-query";
import {
  ApiError,
  QuoteCorrectionPatchEra,
  QuoteCorrectionPatchQuoteForm,
  QuoteCorrectionPatchSourceType,
  QuoteCorrectionPatchStatus,
  QuoteCorrectionPatchVisibility,
  QuoteCorrectionPatchWorkType,
  type QuoteCorrectionPatch,
  type QuoteWithTags,
} from "@workspace/api-client-react";

/** Fields the workspace may enrich QuoteWithTags with (optional until OpenAPI catches up). */
export type CuratedQuote = QuoteWithTags & {
  language?: string | null;
  quoteForm?: QuoteCorrectionPatch["quoteForm"];
  workType?: QuoteCorrectionPatch["workType"];
  visibility?: QuoteCorrectionPatch["visibility"];
  sourceType?: QuoteCorrectionPatch["sourceType"];
  containsProfanity?: boolean;
  containsSexualContext?: boolean;
  ownerUsername?: string | null;
};

export const ERA_VALUES = Object.values(QuoteCorrectionPatchEra);
export const SOURCE_TYPE_VALUES = Object.values(QuoteCorrectionPatchSourceType);
export const QUOTE_FORM_VALUES = Object.values(QuoteCorrectionPatchQuoteForm);
export const WORK_TYPE_VALUES = Object.values(QuoteCorrectionPatchWorkType);
export const STATUS_VALUES = Object.values(QuoteCorrectionPatchStatus);
export const VISIBILITY_VALUES = Object.values(QuoteCorrectionPatchVisibility);

export const TEXT_LIMIT = 600;
export const codePoints = (s: string) => Array.from(s.trim()).length;

export interface CurationForm {
  text: string;
  author: string;
  era: string;
  year: string;
  work: string;
  sourceType: string;
  language: string;
  quoteForm: string;
  workType: string;
  containsProfanity: boolean;
  containsSexualContext: boolean;
  status: string;
  visibility: string;
  tagIds: number[];
}

export function formFromQuote(q: CuratedQuote): CurationForm {
  return {
    text: q.text ?? "",
    author: q.author ?? "",
    era: q.era ?? "",
    year: q.year == null ? "" : String(q.year),
    work: q.work ?? "",
    sourceType: q.sourceType ?? "",
    language: q.language ?? "",
    quoteForm: q.quoteForm ?? "",
    workType: q.workType ?? "",
    containsProfanity: !!q.containsProfanity,
    containsSexualContext: !!q.containsSexualContext,
    status: q.status ?? "",
    visibility: q.visibility ?? "",
    tagIds: (q.tags ?? []).map((t) => t.id),
  };
}

export function isOriginal(q: CuratedQuote): boolean {
  return q.sourceType === "user_authored";
}

/** Build a patch containing only fields that differ from the stored record. */
export function buildPatch(base: CurationForm, f: CurationForm): QuoteCorrectionPatch {
  const p: QuoteCorrectionPatch = {};
  if (f.text.trim() !== base.text.trim()) p.text = f.text.trim();
  if (f.author.trim() !== base.author.trim()) p.author = f.author.trim();
  if (f.era !== base.era && f.era) p.era = f.era as QuoteCorrectionPatch["era"];
  if (f.year.trim() !== base.year) p.year = f.year.trim() === "" ? null : Number(f.year);
  if (f.work !== base.work) p.work = f.work.trim();
  if (f.sourceType !== base.sourceType && f.sourceType) p.sourceType = f.sourceType as QuoteCorrectionPatch["sourceType"];
  if (f.language !== base.language) p.language = f.language.trim() || null;
  if (f.quoteForm !== base.quoteForm) p.quoteForm = (f.quoteForm || null) as QuoteCorrectionPatch["quoteForm"];
  if (f.workType !== base.workType) p.workType = (f.workType || null) as QuoteCorrectionPatch["workType"];
  if (f.containsProfanity !== base.containsProfanity) p.containsProfanity = f.containsProfanity;
  if (f.containsSexualContext !== base.containsSexualContext) p.containsSexualContext = f.containsSexualContext;
  if (f.status !== base.status && f.status) p.status = f.status as QuoteCorrectionPatch["status"];
  if (f.visibility !== base.visibility && f.visibility) p.visibility = f.visibility as QuoteCorrectionPatch["visibility"];
  const a = [...base.tagIds].sort().join(",");
  const b = [...f.tagIds].sort().join(",");
  if (a !== b) p.tagIds = f.tagIds;
  return p;
}

export function validateForm(f: CurationForm): string | null {
  if (!f.text.trim()) return "Quote text is required.";
  if (codePoints(f.text) > TEXT_LIMIT) return `Quote text exceeds ${TEXT_LIMIT} characters.`;
  if (!f.author.trim()) return "Author is required.";
  if (f.year.trim() !== "" && !/^-?\d+$/.test(f.year.trim())) return "Year must be a whole number (negative for BCE).";
  if (f.tagIds.length > 3) return "Choose at most 3 tags.";
  return null;
}

export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.data as Record<string, unknown> | null;
    const msg = typeof d?.error === "string" ? d.error : typeof d?.message === "string" ? d.message : null;
    if (err.status === 409) return msg ? `Conflict: ${msg}` : "Conflict: this quote changed since you opened it. Reload the workspace and try again.";
    if (err.status === 403 || err.status === 401) return msg ?? "You are not authorised to curate quotes.";
    return msg ?? `Request failed (${err.status}).`;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

export const humanize = (s: string) => s.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (c) => c.toUpperCase());

/** Refresh every surface that could show the corrected / removed quote. */
export function invalidateAfterCuration(qc: QueryClient, removedId: number | null) {
  if (removedId != null) {
    qc.removeQueries({ queryKey: [`/api/quotes/${removedId}`] });
    qc.removeQueries({ queryKey: [`/api/admin/quotes/${removedId}/workspace`] });
  }
  qc.invalidateQueries({
    predicate: ({ queryKey }) => {
      const path = queryKey[0];
      return (
        typeof path === "string" &&
        ["/api/quotes", "/api/profile", "/api/authors", "/api/tags", "/api/themes", "/api/submissions", "/api/community", "/api/admin", "/api/discover", "/api/daily"].some(
          (p) => path.startsWith(p),
        )
      );
    },
  });
}
